import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {Banner, Link, LinkButton} from '@primer/react'
import type {AgentQuotaSnapshot} from '../../types/agent-quota-snapshot'
import styles from './QuotaBanner.module.css'
import {CopilotPlan} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {MANAGE_BUDGET_URL, UPGRADE_PLAN_URL} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'

type QuotaBannerProps = {
  agentQuotaSnapshot?: AgentQuotaSnapshot
}

export function QuotaBanner({agentQuotaSnapshot}: QuotaBannerProps) {
  // Temporary gate the banner behide this FF until the use_token_based_billing logic is implemented.
  if (!copilotFeatureFlags.codingAgentTBBQuotaBanner) return null

  // agentQuotaSnapshot isn't defined when the user has no agent access or quota info is unavailable
  if (!agentQuotaSnapshot) return null

  // Product decision: only show upgrade/manage budget options to individual users
  // since business/enterprise users are managed by org admins and cannot self-serve.
  const plan = agentQuotaSnapshot.copilot_plan
  let isIndividual = true
  if (plan === CopilotPlan.Business || plan === CopilotPlan.Enterprise) {
    isIndividual = false
  }

  const showUpgradePlan = agentQuotaSnapshot.can_upgrade_plan && isIndividual
  const showManageBudget = isIndividual

  const manageBudgetAction = (
    <LinkButton as="a" href={MANAGE_BUDGET_URL}>
      Manage Budget
    </LinkButton>
  )

  const upgradePlanAction = (
    <Link as="a" href={UPGRADE_PLAN_URL}>
      Upgrade Plan
    </Link>
  )

  // Keep parity with copilot-chat banner action hierarchy:
  // when both are available, de-emphasize upgrade and keep Manage Budget as strong CTA.
  const primaryAction = showUpgradePlan ? (
    <>
      {upgradePlanAction}
      {manageBudgetAction}
    </>
  ) : showManageBudget ? (
    manageBudgetAction
  ) : undefined

  // Business / enterprise: managed by org admin, user cannot self-serve → no button, show admin message
  const requestUsageMessage = !isIndividual ? ' Request your admin for additional usage.' : ''

  // Show critical banner if quota is insufficient to start session.
  // Still allow message submission since check is cache-based.
  if (!agentQuotaSnapshot.can_start_session) {
    return (
      <div className={styles.container} data-testid="quota-banner">
        <Banner
          variant="critical"
          title="Insufficient quota"
          hideTitle
          description={`The remaining usage limit is insufficient to start a session.${requestUsageMessage}`}
          primaryAction={primaryAction}
        />
      </div>
    )
  }

  // percent_remaining is normalized to [0, 1].
  // Show warning banner when remaining quota is at or below 25%.
  const shouldShowWarning = agentQuotaSnapshot.percent_remaining >= 0 && agentQuotaSnapshot.percent_remaining <= 0.25

  // Product decision: use bucketed, fixed copy instead of dynamic percentages.
  // - At/under 25% remaining: "used 75%"
  // - At/under 10% remaining: "used 90%"
  const warningMessage =
    agentQuotaSnapshot.percent_remaining <= 0.1
      ? 'You have used 90% of your monthly usage limit.'
      : 'You have used 75% of your monthly usage limit.'

  if (shouldShowWarning) {
    return (
      <div className={styles.container} data-testid="quota-banner">
        <Banner
          variant="info"
          title="Usage limit warning"
          hideTitle
          description={warningMessage}
          primaryAction={primaryAction}
        />
      </div>
    )
  }

  return null
}
